/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Injector } from "@angular/core";
import { BusEvent } from "@html-core";
import { BlastWmks } from "../common/blast-wmks.service";
import { BlastOption } from "./blast-option";
import { isMKSSessionClosedNormally, webMksReconnectFailure } from "./wmks-error-handler";

export class ChromeBlastSessionInstance extends BlastWmks {
   private numLockStatus;
   constructor(injector: Injector, opt: BlastOption) {
      super(injector, opt);
      const certListener = this.eventBusService
         .listen(BusEvent.ChromeSelfCertAccepted.MSG_TYPE)
         .subscribe((msg: BusEvent.ChromeSelfCertAccepted) => {
            this.initialize(this.url, this.settings);
            certListener.unsubscribe();
         });

      this.eventBusService.listen("numLockStatus").subscribe((msg) => {
         this.numLockStatus = msg.data;
      });
   }

   public static getInstance = (injector, opt) => {
      return new ChromeBlastSessionInstance(injector, opt);
   };

   public onInitialize = () => {
      this.option.allowMobileExtendedKeypad = false;
      this.option.allowMobileKeyboardInput = false;
      this.option.allowMobileTrackpad = false;
   };

   public OnWmksConnected = () => {
      super.OnWmksConnected();
      if (this.numLockStatus) {
         this.wmksContainer.wmks("sendKeyCotoggleTenKeysNumpaddes", true);
      }
   };

   public reconnectHandler = (code: number, closeReason: number) => {
      if (this.shouldTryCert()) {
         this.logger.info("showing cert page");
         this.tryAcceptSelfSignCert(code);
      } else if (this.isIdleTimeout()) {
         this.logger.info(
            "session disconnected due to idle timeout, " +
               "don't show disconnected dialog since there is a idle timeout dialog"
         );
         this.destroy();
      } else if (webMksReconnectFailure(code)) {
         this.logger.info("session reconnection failed");
         this.closeDisconnectedDialog("old");
         this._wmksService.showError(
            this,
            this.translate._T("DESKTOP_DISCONNECTED_T"),
            this.translate._T("DESKTOP_DISCONNECTED_M") + this.translate._T("RECONNECT_FAIL_M")
         );
         this.destroy();
      } else if (this.canReconnect(code, closeReason)) {
         this.logger.info("try one more reconnection");
         if (
            !this.modalDialogService.isDialogOpen(this._disconnectedDialogId) &&
            this._runningReconnectInBackGround !== true
         ) {
            this._openDisconnectedDialog(true);
         }
         this._startReconnection();
      } else {
         this.logger.info("this session has been ended");
         if (closeReason === 30) {
            // VDPCONNECT_SERVER_SHADOW_SESSION_ENDED, code =1000. closeReason 30.
            this.logger.info("show shadow session disconnect error");
            this.closeDisconnectedDialog("old");
            this._wmksService.showError(
               this,
               this.translate._T("DESKTOP_DISCONNECTED_T"),
               this.translate._T("DESKTOP_DISCONNECTED_M") + this.translate._T("COLLABORATION_SESSION_ENDED")
            );
         } else if (isMKSSessionClosedNormally(code, closeReason)) {
            //when normally log off the desktop, there will not pop up the disconnect dialogue.
            //This behaviour change is consist with native client.
            if (closeReason === 13 || (code === 1000 && (!closeReason || closeReason === -1))) {
               // Show error dialog when steal the session.
               this.closeDisconnectedDialog("old");
               this._wmksService.showError(
                  this,
                  this.translate._T("DESKTOP_DISCONNECTED_T"),
                  this.translate._T("DESKTOP_DISCONNECTED_M")
               );
            }
            this.logger.info("user triggered disconnect, closeReason: " + closeReason);
         } else {
            this.logger.info("show general disconnect error");
            this.closeDisconnectedDialog("old");
            this._wmksService.showError(
               this,
               this.translate._T("DESKTOP_DISCONNECTED_T"),
               this.translate._T("DESKTOP_DISCONNECTED_M")
            );
         }
         this.destroy();
      }
   };
}
